import { db, newId } from './matudb';

const TABLE = 'app_documents';
const listeners = new Map();
let cache = { at: 0, rows: [] };

function cleanPath(path) {
  return String(path || '').replace(/^\/+|\/+$/g, '');
}

function makeRef(path) {
  const normalized = cleanPath(path);
  return {
    path: normalized,
    key: normalized.split('/').filter(Boolean).pop() || null,
  };
}

export function getDatabase() {
  return {};
}

export function ref(_database, path) {
  return makeRef(path);
}

export function child(parent, path) {
  return makeRef(`${parent.path}/${path}`);
}

export function serverTimestamp() {
  return { '.sv': 'timestamp' };
}

function normalizeValue(value) {
  if (value && typeof value === 'object' && value['.sv'] === 'timestamp') {
    return new Date().toISOString();
  }
  if (Array.isArray(value)) return value.map(normalizeValue);
  if (value && typeof value === 'object') {
    const next = {};
    Object.keys(value).forEach((key) => {
      next[key] = normalizeValue(value[key]);
    });
    return next;
  }
  return value;
}

function snapshot(path, value) {
  return {
    key: makeRef(path).key,
    exists: () => value !== null && value !== undefined,
    val: () => value,
    forEach(fn) {
      if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
      return Object.keys(value).some((key) => fn({
        key,
        val: () => value[key],
        exists: () => true,
      }) === true);
    },
  };
}

async function loadRows() {
  if (Date.now() - cache.at < 1200) return cache.rows;
  const { data, error } = await db.from(TABLE).select('id,data').limit(5000);
  if (error) throw new Error(error.message || 'No se pudo leer MatuDB');
  cache = { at: Date.now(), rows: data || [] };
  return cache.rows;
}

function invalidate() {
  cache.at = 0;
}

function rowsUnder(rows, path) {
  if (!path) return rows;
  return rows.filter((row) => row.id === path || String(row.id).startsWith(`${path}/`));
}

function materialize(rows, path) {
  const exact = rows.find((row) => row.id === path);
  const children = rows.filter((row) => String(row.id).startsWith(`${path}/`));
  if (!exact && children.length === 0) return null;

  let base = exact ? exact.data : {};
  if (exact && (typeof exact.data !== 'object' || exact.data === null || Array.isArray(exact.data))) {
    if (children.length === 0) return exact.data;
    base = {};
  } else {
    base = { ...(base || {}) };
  }

  children.forEach((row) => {
    const rest = String(row.id).slice(path.length + 1).split('/');
    let cursor = base;
    rest.forEach((part, index) => {
      if (index === rest.length - 1) {
        cursor[part] = row.data;
        return;
      }
      if (!cursor[part] || typeof cursor[part] !== 'object' || Array.isArray(cursor[part])) {
        cursor[part] = {};
      }
      cursor = cursor[part];
    });
  });
  return base;
}

function applyConstraints(value, constraints) {
  if (!constraints.length || !value || typeof value !== 'object' || Array.isArray(value)) {
    return value;
  }
  let entries = Object.entries(value);
  const order = constraints.find((item) => item.type === 'orderByChild');
  const equals = constraints.find((item) => item.type === 'equalTo');
  const start = constraints.find((item) => item.type === 'startAt');
  const end = constraints.find((item) => item.type === 'endAt');
  const limit = constraints.find((item) => item.type === 'limitToLast');
  const field = order?.field;
  if (field && equals) {
    entries = entries.filter(([, item]) => item && item[field] === equals.value);
  }
  if (field && (start || end)) {
    entries = entries.filter(([, item]) => {
      const current = item?.[field];
      if (start && current < start.value) return false;
      if (end && current > end.value) return false;
      return true;
    });
  }
  if (field) {
    entries.sort((a, b) => {
      const av = a[1]?.[field];
      const bv = b[1]?.[field];
      if (av === bv) return 0;
      return av > bv ? 1 : -1;
    });
  }
  if (limit) entries = entries.slice(-limit.n);
  return Object.fromEntries(entries);
}

async function readPath(path, constraints = []) {
  const rows = rowsUnder(await loadRows(), path);
  return applyConstraints(materialize(rows, path), constraints);
}

async function upsert(path, data) {
  const payload = { data: normalizeValue(data), path };
  const { data: existing, error: readError } = await db.from(TABLE).select('id').eq('id', path).limit(1);
  if (readError) throw new Error(readError.message || 'No se pudo leer el documento');
  if (Array.isArray(existing) && existing.length > 0) {
    const { error } = await db.from(TABLE).eq('id', path).update(payload);
    if (error) throw new Error(error.message || 'No se pudo actualizar');
  } else {
    const { error } = await db.from(TABLE).insert({ id: path, ...payload });
    if (error) throw new Error(error.message || 'No se pudo guardar');
  }
  invalidate();
}

async function removePath(path) {
  const rows = rowsUnder(await loadRows(), path);
  await Promise.all(rows.map(async (row) => {
    const { error } = await db.from(TABLE).eq('id', row.id).delete();
    if (error) throw new Error(error.message || 'No se pudo borrar');
  }));
  invalidate();
}

function notify(path) {
  listeners.forEach((bucket, listenPath) => {
    const related = listenPath === path
      || path.startsWith(`${listenPath}/`)
      || listenPath.startsWith(`${path}/`);
    if (related) bucket.forEach((entry) => entry.emit());
  });
}

export function onValue(target, callback, onError) {
  const path = target.path;
  const constraints = target._constraints || [];
  if (!listeners.has(path)) listeners.set(path, new Map());
  const bucket = listeners.get(path);
  const emit = async () => {
    try {
      callback(snapshot(path, await readPath(path, constraints)));
    } catch (error) {
      if (onError) onError(error);
      else console.error(error);
    }
  };
  bucket.set(callback, { emit });
  emit();
  const timer = setInterval(emit, 8000);
  return () => {
    clearInterval(timer);
    bucket.delete(callback);
  };
}

export function onChildAdded(target, callback) {
  const seen = new Set();
  return onValue(target, (snap) => {
    const value = snap.val();
    if (!value || typeof value !== 'object') return;
    Object.keys(value).forEach((key) => {
      if (seen.has(key)) return;
      seen.add(key);
      callback({ key, val: () => value[key], exists: () => true });
    });
  });
}

export function off(target, _event, callback) {
  const bucket = listeners.get(target.path);
  if (!bucket) return;
  if (callback) bucket.delete(callback);
  else bucket.clear();
}

export async function get(target) {
  return snapshot(target.path, await readPath(target.path, target._constraints || []));
}

export async function set(target, value) {
  await removePath(target.path);
  await upsert(target.path, value);
  notify(target.path);
}

export async function update(target, patch) {
  const current = await readPath(target.path);
  const base = current && typeof current === 'object' && !Array.isArray(current) ? { ...current } : {};
  Object.entries(patch || {}).forEach((entry) => {
    const [key, raw] = entry;
    const value = normalizeValue(raw);
    if (key.includes('/')) {
      const parts = key.split('/');
      let cursor = base;
      parts.forEach((part, index) => {
        if (index === parts.length - 1) {
          if (value === null) delete cursor[part];
          else cursor[part] = value;
          return;
        }
        if (!cursor[part] || typeof cursor[part] !== 'object') cursor[part] = {};
        cursor = cursor[part];
      });
      return;
    }
    if (value === null) delete base[key];
    else base[key] = value;
  });
  await upsert(target.path, base);
  notify(target.path);
}

export async function remove(target) {
  await removePath(target.path);
  notify(target.path);
}

export function push(target, value) {
  const key = newId();
  const next = makeRef(`${target.path}/${key}`);
  if (value === undefined) return next;
  const pending = set(next, value);
  return Object.assign(pending, { key, ref: next });
}

export function query(target, ...constraints) {
  return { ...target, _constraints: constraints };
}

export function orderByChild(field) {
  return { type: 'orderByChild', field };
}

export function limitToLast(n) {
  return { type: 'limitToLast', n };
}

export function equalTo(value) {
  return { type: 'equalTo', value };
}

export function startAt(value) {
  return { type: 'startAt', value };
}

export function endAt(value) {
  return { type: 'endAt', value };
}

export const database = {};
